import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { QRCode } from 'react-qr-code';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { buildAppInstallUrl } from '../../constants/appInstall';
import type { AppInstallSrc } from '../../constants/appInstall';
import { safeAnalytics } from '../../utils/analytics';

interface AppInstallQRProps {
  /** Which placement this is. Travels in the URL so scans can be split by it. */
  readonly src: AppInstallSrc;
  /** QR pixel size. */
  readonly size?: number;
  /** Caption under the QR. */
  readonly caption?: string;
  /**
   * What "Use the browser instead" swaps in, for someone who would rather not
   * install the app. Only mounted once asked for, so a magic-link QR passed
   * here mints its sign-in token on that click and never on page load.
   * Omit it and the link is not offered.
   */
  readonly browserFallback?: ReactNode;
}

/**
 * The QR code for getting the Bower mobile app.
 *
 * It encodes bowerlabs.ai/app (see constants/appInstall.ts), which sends the
 * phone to the right store. Like MobileAccessQR it carries no credential, so
 * it is safe on any surface; the user signs in inside the app.
 */
export function AppInstallQR({
  src,
  size = 160,
  caption = 'Scan with your phone to get the Bower app',
  browserFallback,
}: AppInstallQRProps) {
  const [showBrowser, setShowBrowser] = useState(false);
  // Each toggle button unmounts the moment it is pressed, so focus would fall
  // to the page body. After a swap, move it to the button that replaced it.
  const swappedRef = useRef(false);
  const toBrowserRef = useRef<HTMLButtonElement>(null);
  const toAppRef = useRef<HTMLButtonElement>(null);
  const swap = (browser: boolean) => {
    swappedRef.current = true;
    setShowBrowser(browser);
  };

  useEffect(() => {
    if (!swappedRef.current) return;
    (showBrowser ? toAppRef : toBrowserRef).current?.focus();
  }, [showBrowser]);

  useEffect(() => {
    safeAnalytics.track(ANALYTICS_EVENTS.APP_INSTALL_QR_DISPLAYED, { src });
  }, [src]);

  if (showBrowser && browserFallback) {
    return (
      <VStack spacing={1} align="center">
        {browserFallback}
        <Button ref={toAppRef} size="xs" variant="link" onClick={() => swap(false)}>
          Get the app instead
        </Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={2} align="center" py={1}>
      {/* bg="white" is a Chakra colour alias (always #FFFFFF): a QR needs a
          white quiet zone to scan, in dark mode too. role=img + aria-label
          name the svg, which react-qr-code leaves unlabelled. */}
      <Box
        role="img"
        aria-label={`QR code: ${caption}`}
        bg="white"
        p={2}
        borderRadius="md"
        border="1px"
        borderColor="border.default"
      >
        <QRCode value={buildAppInstallUrl(src)} size={size} />
      </Box>
      <Text fontSize="xs" color="text.muted" textAlign="center" maxW="220px">
        {caption}
      </Text>
      {browserFallback && (
        <Button
          ref={toBrowserRef}
          size="xs"
          variant="link"
          onClick={() => {
            safeAnalytics.track(ANALYTICS_EVENTS.APP_INSTALL_QR_BROWSER_CHOSEN, { src });
            swap(true);
          }}
        >
          Use Bower in your phone&apos;s browser instead
        </Button>
      )}
    </VStack>
  );
}
